import React, { useEffect, useState } from "react";
import api from "../Service/api";

const ProductionDashboard = () => {
  const [records, setRecords] = useState([]);
  const [maxSteps, setMaxSteps] = useState(0);
  const [loading, setLoading] = useState(false);

  const getLocalDate = () => {
    const now = new Date();
    // Get local year, month, day
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [date, setDate] = useState(getLocalDate);

  useEffect(() => {
    fetchProductionData();
  }, [date]);

  const fetchProductionData = () => {
    setLoading(true);
    api.get(`/production/dashboard/production/daily?date=${date}`)
      .then(res => {
        setRecords(res.data || []);
        const max = res.data?.reduce((acc, r) => Math.max(acc, r.steps?.length || 0), 0) || 0;
        setMaxSteps(max);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  // Convert seconds to hours:minutes:seconds format
  const toHoursMinutesSeconds = (seconds) => {
    if (!seconds) return "0s";
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
    } else {
      return `${secs}s`;
    }
  };

  // For step times (shorter format)
  const stepTimeToHoursMinutesSeconds = (seconds) => {
    if (!seconds) return "0s";
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const calculateSummary = () => {
    const totalQuantity = records.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0);
    const totalTimeSeconds = records.reduce((sum, r) => sum + (parseInt(r.totalDurationInSeconds) || 0), 0);
    
    return {
      totalRecords: records.length,
      totalQuantity,
      totalTime: toHoursMinutesSeconds(totalTimeSeconds),
      totalTimeSeconds, // Keep seconds for calculations
      maxSteps
    };
  };

  const handlePrint = () => {
    const summary = calculateSummary();
    const printWindow = window.open('', '_blank', 'width=1100,height=700');
    
    const tableRows = records.map(r => {
      const stepsHtml = r.steps?.map((step, idx) => `
        <td style="border: 1px solid #ddd; padding: 6px;">${step.stepName}</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${stepTimeToHoursMinutesSeconds(step.durationInSeconds)}</td>
      `).join('') || '';
      
      const emptyStepsHtml = Array.from({ length: maxSteps - (r.steps?.length || 0) }).map(() => `
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">-</td>
        <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">-</td>
      `).join('');
      
      return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${r.companyName || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${r.productName || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${r.employeeName || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${r.quantity || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${toHoursMinutesSeconds(r.totalDurationInSeconds || 0)}</td>
          ${stepsHtml}${emptyStepsHtml}
        </tr>
      `;
    }).join('');

    const stepHeadersHtml = Array.from({ length: maxSteps }).map((_, idx) => `
      <th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold;">Step ${idx + 1}</th>
      <th style="border: 1px solid #ddd; padding: 10px; background: #f5f5f5; font-weight: bold;">Time</th>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Production Report - ${date}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #000; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { color: #333; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .summary { margin-top: 30px; padding: 15px; background: #f9f9f9; border-radius: 5px; }
          .summary h3 { margin-top: 0; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          @media print { body { padding: 10px; } @page { size: landscape; margin: 0.5in; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Daily Production Report</h1>
          <p><strong>Date:</strong> ${date} | <strong>Total Records:</strong> ${records.length}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Product Name</th>
              <th>Employee Name</th>
              <th>Quantity</th>
              <th>Total Time</th>
              ${stepHeadersHtml}
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        
        ${records.length > 0 ? `
          <div class="summary">
            <h3>Summary</h3>
            <p><strong>Total Records:</strong> ${summary.totalRecords} | 
               <strong>Total Quantity:</strong> ${summary.totalQuantity} | 
               <strong>Total Time:</strong> ${summary.totalTime} | 
               <strong>Max Steps:</strong> ${summary.maxSteps}</p>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>Printed on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 500);
            }, 250);
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const summary = calculateSummary();

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
      <h1>Daily Production Report</h1>
          
      {/* Header Section */}
      <div style={{ marginBottom: "30px", borderBottom: "1px solid #e0e0e0", paddingBottom: "15px" }}>
        <div>
          <button
            onClick={handlePrint}
            disabled={records.length === 0}
            style={{
              padding: "10px 20px",
              backgroundColor: records.length === 0 ? "#cccccc" : "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: records.length === 0 ? "not-allowed" : "pointer",
              fontWeight: "500",
              fontSize: "14px"
            }}
          >
            🖨️ Print Report
          </button>
        </div>
        <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>View and analyze daily production records</p>
      </div>

      {/* Date Filter */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        gap: "15px", 
        marginBottom: "30px",
        padding: "15px",
        background: "#f8f9fa",
        borderRadius: "5px",
        border: "1px solid #dee2e6"
      }}>
        <label style={{ fontWeight: "500", color: "#495057", fontSize: "15px" }}>
          Select Date:
        </label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{
            padding: "8px 12px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            fontSize: "14px",
            color: "#000",
            backgroundColor: "#fff"
          }}
        />
        <div style={{ marginLeft: "auto", fontSize: "14px", color: "#6c757d" }}>
          Showing data for: <span style={{ fontWeight: "500", color: "#495057" }}>{date}</span>
        </div>
      </div>

      {/* Summary Section */}
      {records.length > 0 && (
        <div style={{ 
          marginBottom: "30px",
          padding: "20px",
          background: "#fff",
          borderRadius: "6px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          <h2 style={{ 
            margin: "0 0 20px 0", 
            fontSize: "18px", 
            color: "#333",
            borderBottom: "2px solid #007bff",
            paddingBottom: "10px"
          }}>
            Production Summary
          </h2>
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px"
          }}>
            <div style={{ textAlign: "center", flex: "1", minWidth: "150px" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: "#495057", marginBottom: "8px" }}>Total Records</div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#007bff" }}>{summary.totalRecords}</div>
            </div>
            
            <div style={{ textAlign: "center", flex: "1", minWidth: "150px" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: "#495057", marginBottom: "8px" }}>Total Quantity</div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#28a745" }}>{summary.totalQuantity}</div>
            </div>
            
            <div style={{ textAlign: "center", flex: "1", minWidth: "150px" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: "#495057", marginBottom: "8px" }}>Total Time</div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#6f42c1" }}>{summary.totalTime}</div>
            </div>
            
            <div style={{ textAlign: "center", flex: "1", minWidth: "150px" }}>
              <div style={{ fontSize: "14px", fontWeight: "bold", color: "#495057", marginBottom: "8px" }}>Max Steps</div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#fd7e14" }}>{summary.maxSteps}</div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ 
            display: "inline-block",
            width: "40px",
            height: "40px",
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}></div>
          <p style={{ marginTop: "15px", color: "#666" }}>Loading production data...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : records.length === 0 ? (
        <div style={{ 
          textAlign: "center", 
          padding: "40px",
          border: "2px dashed #dee2e6",
          borderRadius: "6px",
          background: "#f8f9fa"
        }}>
          <div style={{ fontSize: "48px", color: "#adb5bd", marginBottom: "15px" }}>📊</div>
          <p style={{ fontSize: "16px", color: "#495057", marginBottom: "5px" }}>
            No production records found for {date}
          </p>
          <p style={{ fontSize: "14px", color: "#6c757d" }}>
            Select a different date to view production data
          </p>
        </div>
      ) : (
        <div style={{ 
          overflowX: "auto",
          borderRadius: "6px",
          border: "1px solid #e0e0e0",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
        }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            minWidth: "1000px"
          }}>
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th style={{ 
                  borderBottom: "2px solid #dee2e6", 
                  padding: "12px", 
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "#495057",
                  fontSize: "14px"
                }}>Company Name</th>
                <th style={{ 
                  borderBottom: "2px solid #dee2e6", 
                  padding: "12px", 
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "#495057",
                  fontSize: "14px"
                }}>Product Name</th>
                <th style={{ 
                  borderBottom: "2px solid #dee2e6", 
                  padding: "12px", 
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "#495057",
                  fontSize: "14px"
                }}>Employee Name</th>
                <th style={{ 
                  borderBottom: "2px solid #dee2e6", 
                  padding: "12px", 
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "#495057",
                  fontSize: "14px"
                }}>Quantity</th>
                <th style={{ 
                  borderBottom: "2px solid #dee2e6", 
                  padding: "12px", 
                  textAlign: "left",
                  fontWeight: "bold",
                  color: "#495057",
                  fontSize: "14px"
                }}>Total Time</th>
                {Array.from({ length: maxSteps }).map((_, idx) => (
                  <React.Fragment key={idx}>
                    <th style={{ 
                      borderBottom: "2px solid #dee2e6", 
                      padding: "12px", 
                      textAlign: "left",
                      fontWeight: "bold",
                      color: "#495057",
                      fontSize: "14px"
                    }}>Step ${idx + 1}</th>
                    <th style={{ 
                      borderBottom: "2px solid #dee2e6", 
                      padding: "12px", 
                      textAlign: "left",
                      fontWeight: "bold",
                      color: "#495057",
                      fontSize: "14px"
                    }}>Time</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.recordId} style={{ borderBottom: "1px solid #e0e0e0", background: "#fff" }}>
                  <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", color: "#333" }}>{r.companyName}</td>
                  <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", color: "#333" }}>{r.productName}</td>
                  <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", color: "#666" }}>{r.employeeName || "-"}</td>
                  <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", textAlign: "center", color: "#333", fontWeight: "500" }}>{r.quantity || "-"}</td>
                  <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", textAlign: "center", color: "#333", fontWeight: "500" }}>
                    {toHoursMinutesSeconds(r.totalDurationInSeconds)}
                  </td>

                  {r.steps?.map((step, idx) => (
                    <React.Fragment key={idx}>
                      <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", color: "#666" }}>{step.stepName}</td>
                      <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", textAlign: "center", color: "#666" }}>
                        {stepTimeToHoursMinutesSeconds(step.durationInSeconds)}
                      </td>
                    </React.Fragment>
                  ))}

                  {Array.from({ length: maxSteps - (r.steps?.length || 0) }).map((_, i) => (
                    <React.Fragment key={i + (r.steps?.length || 0)}>
                      <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", textAlign: "center", color: "#dee2e6" }}>-</td>
                      <td style={{ padding: "12px", borderBottom: "1px solid #e0e0e0", textAlign: "center", color: "#dee2e6" }}>-</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductionDashboard;